import {
    HostArtifactAdditionalData,
    IHostArtifact,
    IInternalLinkedArtifactDisplayData,
    ILinkedArtifactsCache
} from "Presentation/Scripts/TFS/Controls/LinkedArtifacts/Contracts";
import { ArtifactResolver, IArtifactResult } from "Presentation/Scripts/TFS/Controls/LinkedArtifacts/Logic/ArtifactResolver";
import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import { ILinkedArtifact } from "TFS/WorkItemTracking/ExtensionContracts";
import { ArtifactTypeNames, ToolNames } from "VSS/Artifacts/Constants";
import { Debug } from "VSS/Diag";
import { LinkTypeName } from "WorkItemTracking/Scripts/OM/WorkItemConstants";

/**
 * Very basic cache for the provider
 */
class ParentArtifactCache implements ILinkedArtifactsCache {
    private _cache: IDictionaryStringTo<IInternalLinkedArtifactDisplayData>;

    constructor() {
        this._cache = {};
    }

    /**
     * Set value for cache, existing values should be overriden
     * @param key Key for cache
     * @param value Value to set
     */
    set(key: string, value: IInternalLinkedArtifactDisplayData): void {
        this._cache[key] = value;
    }

    /**
     * Get resolved artifact data from cache
     * @param key Key to get value for
     * @returns Value for key
     */
    get(key: string): IInternalLinkedArtifactDisplayData {
        return this._cache[key];
    }

    /**
     * Invalidates the specified cache entry
     * @param key Key to invalidate
     */
    invalidate(key: string): void {
        delete this._cache[key];
    }
}

export interface IParentFieldInfo {
    id: Number;
    type: string;
    project: string;
    title: string;
    error?: Error;
}

/**
 * Provider for data for 'parent's for a work item.
 */
export class ParentFieldProvider {
    private _tfsContext: TfsContext;
    private _projectName: string;
    private _artifactCache: ParentArtifactCache;

    constructor(tfsContext: TfsContext, projectName: string) {
        Debug.assertParamIsObject(tfsContext, "tfsContext");

        this._tfsContext = tfsContext;
        this._projectName = projectName;
    }

    /**
     * Get the specified work item info from the cache.
     */
    public get(id: number): IParentFieldInfo | undefined {
        if (this._artifactCache !== undefined) {
            const cacheItem = ArtifactResolver.getArtifactCacheEntry(this._artifactCache, this.createLinkedArtifact(id), []);

            if (cacheItem) {
                return this.createParentInfo(cacheItem);
            }
        }

        return undefined;
    }

    /**
     * load the specified work item ids data into the cache
     */
    public load(ids: number[]): IPromise<IParentFieldInfo[]> {
        return new Promise((resolve, reject) => {
            if (ids.length) {
                const artifacts: ILinkedArtifact[] = [];

                ids.forEach((id: number) => {
                    artifacts.push(this.createLinkedArtifact(id));
                });

                if (!this._artifactCache) {
                    this._artifactCache = new ParentArtifactCache();
                }

                ArtifactResolver.getInstance()
                    .resolveArtifacts(artifacts, [], this.createHostArtifact(), this._tfsContext, undefined, this._artifactCache)
                    .then((result: IArtifactResult) => {
                        const retval: IParentFieldInfo[] = [];
                        if (result.resolvedArtifacts) {
                            result.resolvedArtifacts.forEach((artifact: IInternalLinkedArtifactDisplayData) => {
                                if (!artifact.error) {
                                    retval.push(this.createParentInfo(artifact));
                                } else {
                                    reject(artifact.error);
                                }
                            });
                        }

                        resolve(retval);
                    });
            } else {
                resolve([]);
            }
        });
    }

    /**
     * remove the specified work item from the cache, if it exists
     */
    public invalidate(id: number): void {
        if (this._artifactCache) {
            ArtifactResolver.invalidateArtifactCacheEntry(this._artifactCache, this.createLinkedArtifact(id), []);
        }
    }

    private createParentInfo(artifact: IInternalLinkedArtifactDisplayData): IParentFieldInfo {
        return {
            id: Number(artifact.id),
            type: !artifact.error ? artifact.primaryData.typeName : "",
            project: !artifact.error ? artifact.miscData.TeamProject : "",
            title: !artifact.error ? artifact.primaryData.title : "",
            error: artifact.error
        };
    }

    private createLinkedArtifact(id: number): IInternalLinkedArtifactDisplayData {
        return {
            id: "" + id,
            linkTypeDisplayName: "",
            uri: "" + id,
            linkType: LinkTypeName.Parent_Link_Name,
            tool: ToolNames.WorkItemTracking,
            type: ArtifactTypeNames.WorkItem,
            isColumnDependent: true
        };
    }

    private createHostArtifact(): IHostArtifact {
        return {
            id: "",
            tool: ToolNames.WorkItemTracking,
            type: ArtifactTypeNames.WorkItem,
            additionalData: {
                [HostArtifactAdditionalData.ProjectName]: this._projectName
            }
        };
    }
}
